import formConfigFixture from "@/fixtures/formConfig.json";
import type { FormConfig } from "@/types/FormConfig";
import type { SubmissionSuccessResponse } from "@/types/Payment";
import type {
  ConfirmPaymentInput,
  CreateSessionInput,
  PaymentSession,
} from "./types";

// Preview mode renders the form from a fixture (or window.__FORM_CONFIG__) so an
// editor can see their configuration — there is no backend to talk to and no real
// payment is taken. These stubs stand in for /api/payment/* so the flow reaches
// the Success screen instead of dying on a connection error.

function amountFrom(fields: ConfirmPaymentInput["fields"]): number {
  const amount = (fields.amount as { amount?: unknown } | undefined)?.amount;

  return typeof amount === "number" ? amount : 0;
}

// The session's gateway isn't decorative: PaymentMethodsWithFormConfig resolves
// its buttons with getDriver(session.gateway), which throws on an unknown name.
// Resolve it from the same config previewService serves so both agree.
function previewGateway(): string {
  const config = (window.__FORM_CONFIG__ ??
    formConfigFixture) as unknown as FormConfig;

  return config.payment?.gateway ?? "";
}

export async function createPaymentSession(
  input: CreateSessionInput,
): Promise<PaymentSession> {
  return {
    session_id: `preview-${input.form_id}-${input.amount}`,
    client_secret: "preview-client-secret",
    gateway: previewGateway(),
    config: {},
  };
}

export async function confirmPayment(
  input: ConfirmPaymentInput,
): Promise<SubmissionSuccessResponse> {
  const now = new Date().toISOString();

  return {
    success: true,
    submission: {
      form_id: input.form_id,
      ip: "127.0.0.1",
      created_at: now,
      updated_at: now,
      id: "preview-submission",
    },
    donation_reference: "PREVIEW-0000",
    // Both flagged as missing so the thank-you page shows the Gift Aid and
    // Frontline sign-up forms — they're part of what a preview should reveal.
    missing_components: {
      gift_aid: true,
      signup_frontline: true,
    },
    amount: amountFrom(input.fields),
  };
}
